import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, GripVertical, LinkIcon, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '@/lib/api';
import PhotoUploader from '@/components/PhotoUploader';
import { iconForCategory } from '@/lib/categoryIcons';
import { cn } from '@/lib/cn';
import type { Ingredient, Link as RecipeLink, RecipeInput, Step } from '@shared/schemas';

type Mode = 'create' | 'edit';

type IngredientItem = Ingredient & { _id: string };
type StepItem = Step & { _id: string };
type LinkItem = RecipeLink & { _id: string };

interface EditForm extends Omit<RecipeInput, 'ingredients' | 'steps' | 'links'> {
  ingredients: IngredientItem[];
  steps: StepItem[];
  links: LinkItem[];
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const EMPTY: EditForm = {
  title: '',
  description: '',
  photo_key: null,
  servings: null,
  prep_minutes: null,
  cook_minutes: null,
  ingredients: [],
  steps: [],
  links: [],
  notes: '',
  category_ids: [],
};

function stripIds(form: EditForm): RecipeInput {
  return {
    ...form,
    ingredients: form.ingredients.map(({ _id: _drop, ...rest }) => rest),
    steps: form.steps.map(({ _id: _drop, ...rest }) => rest),
    links: form.links
      .filter((l) => l.url.trim() !== '')
      .map(({ _id: _drop, ...rest }) => rest),
  };
}

export default function AdminEditor({ mode }: { mode: Mode }) {
  const params = useParams<{ id?: string }>();
  const id = params.id ? Number(params.id) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: api.listCategories,
  });
  const recipeQuery = useQuery({
    queryKey: ['admin', 'recipe', id],
    queryFn: () => api.adminGetRecipe(id!),
    enabled: mode === 'edit' && id !== null,
  });

  const [form, setForm] = useState<EditForm>(EMPTY);
  const [loaded, setLoaded] = useState(mode === 'create');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && recipeQuery.data) {
      const r = recipeQuery.data;
      setForm({
        title: r.title,
        description: r.description,
        photo_key: r.photo_key,
        servings: r.servings,
        prep_minutes: r.prep_minutes,
        cook_minutes: r.cook_minutes,
        ingredients: r.ingredients.map((ing) => ({
          _id: newId(),
          name: ing.note ? `${ing.name} (${ing.note})` : ing.name,
          amount: [ing.amount, ing.unit].filter(Boolean).join(' '),
          unit: '',
          note: '',
        })),
        steps: r.steps.map((s) => ({ _id: newId(), text: s.text })),
        links: r.links.map((l) => ({ _id: newId(), url: l.url, label: l.label })),
        notes: r.notes,
        category_ids: r.category_ids,
      });
      setLoaded(true);
    }
  }, [mode, recipeQuery.data]);

  const createMutation = useMutation({
    mutationFn: (input: RecipeInput) => api.adminCreateRecipe(input),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      navigate(`/recipe/${recipe.slug}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Uložení selhalo'),
  });

  const patchMutation = useMutation({
    mutationFn: (input: RecipeInput) => api.adminPatchRecipe(id!, input),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', recipe.slug] });
      navigate('/admin');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Uložení selhalo'),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  if (!loaded) {
    return <div className="h-40 animate-pulse rounded-2xl bg-surface" />;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const payload = stripIds(form);
    if (mode === 'create') createMutation.mutate(payload);
    else patchMutation.mutate(payload);
  }

  function toggleCategory(catId: number) {
    setForm((f) => ({
      ...f,
      category_ids: f.category_ids.includes(catId)
        ? f.category_ids.filter((x) => x !== catId)
        : [...f.category_ids, catId],
    }));
  }

  function updateIngredient(itemId: string, patch: Partial<Ingredient>) {
    setForm((f) => ({
      ...f,
      ingredients: f.ingredients.map((ing) =>
        ing._id === itemId ? { ...ing, ...patch } : ing,
      ),
    }));
  }
  function addIngredient() {
    setForm((f) => ({
      ...f,
      ingredients: [
        ...f.ingredients,
        { _id: newId(), name: '', amount: '', unit: '', note: '' },
      ],
    }));
  }
  function removeIngredient(itemId: string) {
    setForm((f) => ({
      ...f,
      ingredients: f.ingredients.filter((ing) => ing._id !== itemId),
    }));
  }
  function handleIngredientDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const from = f.ingredients.findIndex((i) => i._id === active.id);
      const to = f.ingredients.findIndex((i) => i._id === over.id);
      if (from < 0 || to < 0) return f;
      return { ...f, ingredients: arrayMove(f.ingredients, from, to) };
    });
  }

  function updateStep(itemId: string, text: string) {
    setForm((f) => ({
      ...f,
      steps: f.steps.map((s) => (s._id === itemId ? { ...s, text } : s)),
    }));
  }
  function addStep() {
    setForm((f) => ({ ...f, steps: [...f.steps, { _id: newId(), text: '' }] }));
  }
  function removeStep(itemId: string) {
    setForm((f) => ({ ...f, steps: f.steps.filter((s) => s._id !== itemId) }));
  }
  function handleStepDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const from = f.steps.findIndex((s) => s._id === active.id);
      const to = f.steps.findIndex((s) => s._id === over.id);
      if (from < 0 || to < 0) return f;
      return { ...f, steps: arrayMove(f.steps, from, to) };
    });
  }

  function updateLink(itemId: string, patch: Partial<RecipeLink>) {
    setForm((f) => ({
      ...f,
      links: f.links.map((l) => (l._id === itemId ? { ...l, ...patch } : l)),
    }));
  }
  function addLink() {
    setForm((f) => ({ ...f, links: [...f.links, { _id: newId(), url: '', label: '' }] }));
  }
  function removeLink(itemId: string) {
    setForm((f) => ({ ...f, links: f.links.filter((l) => l._id !== itemId) }));
  }

  const saving = createMutation.isPending || patchMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex items-center gap-2">
        <Link
          to="/admin"
          aria-label="Zpět na recepty"
          className="rounded-full p-2 text-text-muted hover:bg-accent-soft hover:text-accent"
        >
          <ArrowLeft size={18} />
        </Link>
        <h1 className="font-serif text-3xl tracking-tight">
          {mode === 'create' ? 'Nový recept' : 'Úprava receptu'}
        </h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <PhotoUploader
          value={form.photo_key}
          onChange={(key) => setForm((f) => ({ ...f, photo_key: key }))}
        />

        <div className="space-y-4">
          <Field label="Název">
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <Field label="Krátký popis">
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Porce">
              <NumberInput
                value={form.servings}
                onChange={(v) => setForm((f) => ({ ...f, servings: v }))}
              />
            </Field>
            <Field label="Příprava (min)">
              <NumberInput
                value={form.prep_minutes}
                onChange={(v) => setForm((f) => ({ ...f, prep_minutes: v }))}
              />
            </Field>
            <Field label="Vaření (min)">
              <NumberInput
                value={form.cook_minutes}
                onChange={(v) => setForm((f) => ({ ...f, cook_minutes: v }))}
              />
            </Field>
          </div>
          <div className="block space-y-1">
            <span className="text-sm font-medium text-text-muted">Kategorie</span>
            <div className="flex flex-wrap gap-1.5">
              {(categoriesQuery.data ?? []).map((c) => {
                const active = form.category_ids.includes(c.id);
                const Icon = iconForCategory(c);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggleCategory(c.id)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
                      active
                        ? 'border-accent bg-accent text-white'
                        : 'border-border bg-surface text-text-muted hover:border-accent/40',
                    )}
                  >
                    <Icon size={14} />
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="font-serif text-xl">Suroviny</h2>
        {form.ingredients.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-text-muted">
            Zatím žádné suroviny.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleIngredientDragEnd}
          >
            <SortableContext
              items={form.ingredients.map((ing) => ing._id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {form.ingredients.map((ing) => (
                  <IngredientRow
                    key={ing._id}
                    ingredient={ing}
                    onChange={(patch) => updateIngredient(ing._id, patch)}
                    onRemove={() => removeIngredient(ing._id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
        <AddRow onClick={addIngredient} label="Přidat surovinu" />
      </section>

      <section className="space-y-3">
        <h2 className="font-serif text-xl">Postup</h2>
        {form.steps.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-text-muted">
            Zatím žádný postup.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleStepDragEnd}
          >
            <SortableContext
              items={form.steps.map((s) => s._id)}
              strategy={verticalListSortingStrategy}
            >
              <ol className="space-y-2">
                {form.steps.map((step, idx) => (
                  <StepRow
                    key={step._id}
                    step={step}
                    index={idx}
                    onChange={(text) => updateStep(step._id, text)}
                    onRemove={() => removeStep(step._id)}
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        )}
        <AddRow onClick={addStep} label="Přidat krok" />
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl">Odkazy</h2>
          <button
            type="button"
            onClick={addLink}
            className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-3 py-1 text-sm text-accent hover:brightness-95"
          >
            <Plus size={14} /> Přidat odkaz
          </button>
        </div>
        {form.links.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-text-muted">
            Zatím žádné odkazy (YouTube video, recept na webu…).
          </p>
        ) : (
          <ul className="space-y-2">
            {form.links.map((link) => (
              <li
                key={link._id}
                className="flex flex-col gap-2 rounded-xl border border-border bg-surface p-2 sm:flex-row sm:items-center"
              >
                <LinkIcon size={16} className="hidden shrink-0 text-text-muted sm:block" />
                <input
                  type="url"
                  inputMode="url"
                  placeholder="https://…"
                  value={link.url}
                  onChange={(e) => updateLink(link._id, { url: e.target.value })}
                  className={cn(inputCls, 'min-w-0 flex-1')}
                />
                <input
                  type="text"
                  placeholder="popisek (nepovinné)"
                  value={link.label ?? ''}
                  onChange={(e) => updateLink(link._id, { label: e.target.value })}
                  className={cn(inputCls, 'min-w-0 sm:w-48')}
                />
                <button
                  type="button"
                  onClick={() => removeLink(link._id)}
                  className="self-end rounded-full p-1.5 text-text-muted hover:bg-red-50 hover:text-red-700 sm:self-auto"
                  aria-label="Smazat odkaz"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Field label="Poznámky">
        <textarea
          rows={4}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          className={inputCls}
        />
      </Field>

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="sticky bottom-4 z-10 flex justify-end gap-2">
        <Link
          to="/admin"
          className="rounded-full border border-border bg-surface px-5 py-2.5 text-sm font-medium shadow-card hover:bg-accent-soft"
        >
          Zrušit
        </Link>
        <button
          type="submit"
          disabled={saving}
          className="rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-white shadow-card-lg hover:bg-accent/90 disabled:opacity-60"
        >
          {saving ? 'Ukládám…' : 'Uložit'}
        </button>
      </div>
    </form>
  );
}

function IngredientRow({
  ingredient,
  onChange,
  onRemove,
}: {
  ingredient: IngredientItem;
  onChange: (patch: Partial<Ingredient>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ingredient._id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-xl border border-border bg-surface p-2',
        isDragging && 'z-10 shadow-card',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none rounded p-1 text-text-muted hover:bg-accent-soft active:cursor-grabbing"
        aria-label="Přesunout"
      >
        <GripVertical size={16} />
      </button>
      <input
        type="text"
        placeholder="množství"
        value={ingredient.amount ?? ''}
        onChange={(e) => onChange({ amount: e.target.value })}
        className={cn(inputCls, 'w-24 shrink-0')}
      />
      <input
        type="text"
        placeholder="surovina"
        required
        value={ingredient.name}
        onChange={(e) => onChange({ name: e.target.value })}
        className={cn(inputCls, 'min-w-0 flex-1')}
      />
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-1.5 text-text-muted hover:bg-red-50 hover:text-red-700"
        aria-label="Smazat surovinu"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

function StepRow({
  step,
  index,
  onChange,
  onRemove,
}: {
  step: StepItem;
  index: number;
  onChange: (text: string) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step._id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex gap-2 rounded-xl border border-border bg-surface p-2',
        isDragging && 'z-10 shadow-card',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-1 flex h-7 w-7 shrink-0 cursor-grab touch-none items-center justify-center rounded-full bg-accent text-sm font-semibold text-white active:cursor-grabbing"
        aria-label="Přesunout krok"
        title="Táhnutím změníš pořadí"
      >
        {index + 1}
      </button>
      <textarea
        rows={2}
        required
        value={step.text}
        onChange={(e) => onChange(e.target.value)}
        className={cn(inputCls, 'flex-1')}
        placeholder={`Krok ${index + 1}…`}
      />
      <button
        type="button"
        onClick={onRemove}
        className="self-start rounded-full p-1.5 text-text-muted hover:bg-red-50 hover:text-red-700"
        aria-label="Smazat krok"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

// Sits right below the last item of a list (Suroviny / Postup) instead of a
// button up top, so adding the next one doesn't mean scrolling back to the top.
function AddRow({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-border py-2.5 text-sm font-medium text-text-muted hover:border-accent/40 hover:text-accent"
    >
      <Plus size={14} /> {label}
    </button>
  );
}

const inputCls =
  'block w-full rounded-lg border border-border bg-surface-elevated px-3 py-2 text-[15px] outline-none ring-accent/40 focus:ring-2';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-text-muted">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? null : Math.max(0, Number(v)));
      }}
      className={inputCls}
    />
  );
}
